import React from "react";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { AlertData } from "./AlertsUtils/alertsInterfaces";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { createMonitoringAPICall } from "../../utils/createMonitoringAPICall";
import { NavigateFunction } from "react-router-dom";
import { AxiosError } from "axios";
import { getCurrentDateTime } from "../../utils/getCurrentDateAndTime";
import { handleAlertOutcomeError } from "./AlertsUtils/alertOutcomePermission";
import { logger } from "../../../utils/logger";
import { handleAutoAdvanceAfterLabeling } from "./AlertsUtils/handleAutoAdvanceAfterLabeling";
import { getDetectionButtonLayoutClass } from "./AlertsUtils/detectionButtonStyles";
import { VlmFilterType } from "./AlertsUtils/vlmFilters";

export const handleEscalatedTrueThreatClick = (
  alertData: AlertData[],
  setAlertData: Dispatcher<AlertData[]>,
  selectedRow: AlertData,
  setSelectedRow: Dispatcher<AlertData>,
  navigate: NavigateFunction,
  setSelectedButtonsFilled: Dispatcher<any>,
  setPageLoading: Dispatcher<boolean>,
  setButtonClicked: Dispatcher<boolean>,
  // New parameters for auto-advance functionality
  setMP4URL?: Dispatcher<string>,
  setVideoClipLoading?: Dispatcher<boolean>,
  setSeeMoreURL?: Dispatcher<string>,
  setForwardArrowDisabled?: Dispatcher<boolean>,
  setBackArrowDisabled?: Dispatcher<boolean>,
  setShowRefreshButton?: Dispatcher<boolean>,
  setSelectedAlertCameraInfo?: any,
  setIgnoreZonesSelectedAlert?: any,
  userFilters?: any,
  detectionType?: string,
  outcomeFilterType?: string,
  vlmFilterType?: VlmFilterType,
  getCurrentSelectedRowKey?: () => string | null,
  setVideoProcessing?: Dispatcher<boolean>,
  useAsyncGeneration?: boolean,
  syncSelectedRowRef?: (row: any) => void
) => {
  const AxiosUI = createMonitoringAPICall(``);
  const retryFunction = () => {
    handleEscalatedTrueThreatClick(
      alertData,
      setAlertData,
      selectedRow,
      setSelectedRow,
      navigate,
      setSelectedButtonsFilled,
      setPageLoading,
      setButtonClicked,
      setMP4URL,
      setVideoClipLoading,
      setSeeMoreURL,
      setForwardArrowDisabled,
      setBackArrowDisabled,
      setShowRefreshButton,
      setSelectedAlertCameraInfo,
      setIgnoreZonesSelectedAlert,
      userFilters,
      detectionType,
      outcomeFilterType,
      vlmFilterType,
      getCurrentSelectedRowKey,
      setVideoProcessing,
      useAsyncGeneration,
      syncSelectedRowRef
    );
  };
  const postedAlert = {
    approx_capture_timestamp: selectedRow.approx_capture_timestamp,
    window_id: selectedRow.window_id,
    alert_outcome: 4,
    threat_level: 4,
    notes: selectedRow.unresolved_notes,
  };

  AxiosUI.post(`alert/outcome/`, postedAlert).then(
    () => {
      // Update the current row's detection type in alertData
      const newAlertData = [...alertData];
      newAlertData.forEach((object: AlertData, index: number) => {
        if (
          selectedRow.approx_capture_timestamp ===
            object.approx_capture_timestamp &&
          selectedRow.window_id === object.window_id
        ) {
          newAlertData[index].detection_type = "escalated_true_threat";
          newAlertData[index].seen = true;
          newAlertData[index].outcome_number = 4;
          newAlertData[index].notes = selectedRow.unresolved_notes;
          // Update engineering_outcome.outcome_v2 for icon display
          const alertItem = newAlertData[index];
          if (alertItem.engineering_outcome) {
            alertItem.engineering_outcome.outcome_v2 = "escalated_true_threat";
            alertItem.engineering_outcome.outcome = "intruder_detected";
          }
        }
      });

      // Update the selected row object
      const updatedSelectedRow = { ...selectedRow };
      updatedSelectedRow.detection_type = "escalated_true_threat";
      updatedSelectedRow.seen = true;
      updatedSelectedRow.updated = getCurrentDateTime();
      updatedSelectedRow.user = localStorage.username;
      if (updatedSelectedRow.engineering_outcome) {
        updatedSelectedRow.engineering_outcome.outcome_v2 =
          "escalated_true_threat";
        updatedSelectedRow.engineering_outcome.outcome = "intruder_detected";
      }

      // Check if auto-advance props are provided
      if (
        setMP4URL &&
        setVideoClipLoading &&
        setSeeMoreURL &&
        setForwardArrowDisabled &&
        setBackArrowDisabled &&
        setShowRefreshButton &&
        setSelectedAlertCameraInfo &&
        setIgnoreZonesSelectedAlert &&
        userFilters &&
        getCurrentSelectedRowKey &&
        setVideoProcessing &&
        syncSelectedRowRef
      ) {
        // Use auto-advance functionality
        // Note: handleAutoAdvanceAfterLabeling will call handleAlertButtonsFill
        // to set the correct button states for the NEW row
        handleAutoAdvanceAfterLabeling(
          updatedSelectedRow,
          newAlertData,
          setAlertData,
          setSelectedRow,
          setSelectedButtonsFilled,
          setMP4URL,
          setVideoClipLoading,
          setSeeMoreURL,
          setForwardArrowDisabled,
          setBackArrowDisabled,
          setShowRefreshButton,
          setSelectedAlertCameraInfo,
          setIgnoreZonesSelectedAlert,
          navigate,
          userFilters,
          detectionType || "All",
          outcomeFilterType || "All",
          getCurrentSelectedRowKey,
          setVideoProcessing,
          useAsyncGeneration ?? true,
          syncSelectedRowRef,
          vlmFilterType || "All"
        );
      } else {
        // Fallback to original behavior if auto-advance props not provided
        setAlertData(newAlertData);
        localStorage.setItem(
          "selectedRowAlerts",
          JSON.stringify(updatedSelectedRow)
        );
        setSelectedRow(updatedSelectedRow);
        // Only set button fill state in fallback mode (for the current labeled row)
        setSelectedButtonsFilled(() => {
          return {
            true_threat: false,
            true_non_threat: false,
            false_positive: false,
            escalated_true_threat: true,
          };
        });
      }

      setButtonClicked(false);
    },
    (reason: AxiosError) => {
      handleAlertOutcomeError(
        reason,
        navigate,
        retryFunction,
        () => {
          setButtonClicked(false);
          alert("Error saving alert type, please try again");
          logger.log(reason.message || String(reason));
        },
        () => setButtonClicked(false)
      );
    }
  );
};

export const EscalatedTrueThreatButton = (props: any) => {
  const {
    selectdButtonsFilled,
    setSelectedButtonsFilled,
    alertData,
    setAlertData,
    selectedRow,
    setSelectedRow,
    navigate,
    setPageLoading,
    setButtonClicked,
    // New props for auto-advance functionality
    setMP4URL,
    setVideoClipLoading,
    setSeeMoreURL,
    setForwardArrowDisabled,
    setBackArrowDisabled,
    setShowRefreshButton,
    setSelectedAlertCameraInfo,
    setIgnoreZonesSelectedAlert,
    userFilters,
    detectionType,
    outcomeFilterType,
    vlmFilterType,
    getCurrentSelectedRowKey,
    setVideoProcessing,
    useAsyncGeneration,
    syncSelectedRowRef,
    compact,
  } = props;
  const layoutClass = getDetectionButtonLayoutClass({
    compact,
    wide: true,
  });

  const handleClick = () => {
    setButtonClicked(true);
    handleEscalatedTrueThreatClick(
      alertData,
      setAlertData,
      selectedRow,
      setSelectedRow,
      navigate,
      setSelectedButtonsFilled,
      setPageLoading,
      setButtonClicked,
      setMP4URL,
      setVideoClipLoading,
      setSeeMoreURL,
      setForwardArrowDisabled,
      setBackArrowDisabled,
      setShowRefreshButton,
      setSelectedAlertCameraInfo,
      setIgnoreZonesSelectedAlert,
      userFilters,
      detectionType,
      outcomeFilterType,
      vlmFilterType,
      getCurrentSelectedRowKey,
      setVideoProcessing,
      useAsyncGeneration,
      syncSelectedRowRef
    );
  };

  const filledButton = (
    <button
      className={`shadow-button bg-[#7d2e72] border-2	border-[#7d2e72] rounded font-mulish text-white ${layoutClass}`}
      onClick={handleClick}
      disabled={false}
    >
      <PriorityHighIcon /> Escalated True Threat
    </button>
  );

  const unfilledButton = (
    <button
      className={`shadow-button bg-white border-2	border-[#7d2e72] rounded font-mulish text-[#7d2e72] ${layoutClass}`}
      onClick={handleClick}
      disabled={false}
    >
      <PriorityHighIcon /> Escalated True Threat
    </button>
  );

  return selectdButtonsFilled.escalated_true_threat
    ? filledButton
    : unfilledButton;
};
